import React, { useMemo } from "react";
import styled from "styled-components";
import {
  Col,
  Csku,
  Row,
  useWindowSize,
  TabBar,
  Tab,
} from "@commonsku/styles";
import DashboardPageTitle from "../dashboard/DashboardPageTitle";
import DownloadReportButton from "./DownloadReportButton";
import CreateBillButton from "./CreateBillButton";
import CreateCreditMemoButton from "./CreateCreditMemoButton";
import CreateVendorCreditButton from "./CreateVendorCreditButton";
import ExportAllButton from "./ExportAllButton";
import ErrorBoundary from "../ErrorBoundary";
import {
  BrowserRouter,
  Route,
  Routes,
  useParams,
  useNavigate,
  useLocation,
} from "react-router-dom";
import NoMatch from "../NoMatch";
import { ReportProvider } from "../report/report_context";
import { getReportConfig, ReportConfig } from "../report/config";
import ReportFiltersContainer from "../report/filters/ReportFiltersContainer";
import ReportSummaryContainer from "../report/summary/ReportSummaryContainer";
import ReportTable from "../report/table/ReportTable";
import Pagination from "../report/table/Pagination";
import ActionBar from "../report/table/ActionBar";
import Loading from "../report/Loading";
import { useSelector } from "react-redux";
import { getPopups } from "../../selectors";
import createPopup from "../../popup-factory";
import { getIdentityUtils } from '../../utils';
import FinancePodBills from "./FinancePodBills";
import { REPORT_TYPES, getReportTabs, getPageTitle, isBillingReport, isInvoicingReport, isPodBillsReport } from "./report_types";
import { useIdentity, useHasCapabilities } from "../../hooks";

const ReportTableContainer = styled.div`
  position: relative;
`;

const StyledTabBar = styled(TabBar)`
  &&& {
    margin-bottom: 1rem;
  }
`;

const FinanceContainer = () => {
  const popups = useSelector(getPopups);
  const params = useParams();
  const { param } = params;
  const windowHeight = useWindowSize()[1];
  const navigate = useNavigate();
  const identity = useIdentity();
  const { hasUserFlags } = getIdentityUtils(identity);

  const report_type = `finance-${param}`.toUpperCase();

  const reportConfig: ReportConfig = useMemo(
    () => getReportConfig(report_type),
    [report_type],
  );

  const tabs: (keyof typeof REPORT_TYPES)[] = useMemo(
    () => getReportTabs(report_type, hasUserFlags),
    [report_type, hasUserFlags],
  );

  const capabilities = REPORT_TYPES?.[reportConfig?.type]?.capabilities ?? [];
  const hasAccess = useHasCapabilities(capabilities);

  if (!reportConfig || !hasAccess) {
    return null;
  }

  return (
    <ReportProvider
      key={report_type}
      reportConfig={reportConfig}
      containerHeight={windowHeight}
    >
      <Csku as={"div"} className="main-content csku-styles">
        <Row style={{ justifyContent: "space-between" }}>
          <Col
            style={{ display: "flex", alignItems: "center", paddingLeft: 8 }}
          >
            <DashboardPageTitle
              title={getPageTitle(reportConfig.type)}
            />
          </Col>
          <Col padded style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: "1rem" }}>
            <ExportAllButton />
            <DownloadReportButton />
            {isInvoicingReport(report_type) && <CreateCreditMemoButton />}
            {isBillingReport(report_type) && <CreateVendorCreditButton />}
            {isBillingReport(report_type) && !isPodBillsReport(report_type) && <CreateBillButton />}
          </Col>
        </Row>

        <StyledTabBar>
          {tabs.map(
            (tab, index) => (
              <Tab
                key={tab}
                className={reportConfig.type === tab ? 'selected' : ''}
                variant="secondary"
                selected={reportConfig.type === tab}
                onClick={() => {
                  const url = REPORT_TYPES[tab]?.path;
                  if (url) {
                    navigate(url);
                  }
                }}
              >
                {REPORT_TYPES[tab]?.title}
              </Tab>
            )
          )}
        </StyledTabBar>
        <ReportTableContainer>
          {isPodBillsReport(report_type) ? (
            <FinancePodBills />
          ) : (
            <>
              <ReportFiltersContainer />
              <ReportSummaryContainer />
              <ReportTable />
              <Pagination />
              <Loading />
            </>
          )}
        </ReportTableContainer>

        {isPodBillsReport(report_type) ? null : <ActionBar />}
        {popups ? popups.map((p, idx) => createPopup(p, idx)) : null}
      </Csku>
    </ReportProvider>
  );
};

const NoMatchWrapper = () => {
  const location = useLocation();
  return <NoMatch location={location} />;
};

const FinancePageRouter = ({ basename }: { basename?: string }) => {
  return (
    <BrowserRouter basename={basename}>
      <Routes>
        <Route
          path="/finance/:param"
          element={<FinanceContainer />}
          ErrorBoundary={ErrorBoundary}
        />
        <Route
          path={"*"}
          element={<NoMatchWrapper />}
          ErrorBoundary={ErrorBoundary}
        />
      </Routes>
    </BrowserRouter>
  );
};

export default FinancePageRouter;
